import type {
  ShortTermCorrosionCellBounds,
  ShortTermRateFeatureBounds,
  ShortTermRateFeatureCell,
  ShortTermRateFeatureGridMetadata,
} from './types'

export type DisjointSetRootResult = {
  rootIndex: number
  parentIndexByIndex: number[]
}

export function findDisjointSetRoot(
  parentIndexByIndex: readonly number[],
  index: number
): DisjointSetRootResult {
  let root = index
  while (parentIndexByIndex[root] !== root) {
    root = parentIndexByIndex[root]
  }

  const compressedParentIndexByIndex = parentIndexByIndex.slice()
  let currentIndex = index

  while (compressedParentIndexByIndex[currentIndex] !== currentIndex) {
    const nextIndex = compressedParentIndexByIndex[currentIndex]
    compressedParentIndexByIndex[currentIndex] = root
    currentIndex = nextIndex
  }

  return { rootIndex: root, parentIndexByIndex: compressedParentIndexByIndex }
}

export function getExpandedBoundsBucketKeys({
  bounds,
  grid,
  expansionFeet,
}: {
  bounds: ShortTermRateFeatureBounds
  grid: ShortTermRateFeatureGridMetadata
  expansionFeet: number
}): string[] {
  const bucketSizeFeet = Math.max(
    expansionFeet,
    grid.binWidthFeet,
    grid.binHeightFeet
  )
  const minXBucket = Math.floor(
    (bounds.minXFeet - expansionFeet - grid.xStartFeet) / bucketSizeFeet
  )
  const maxXBucket = Math.floor(
    (bounds.maxXFeet + expansionFeet - grid.xStartFeet) / bucketSizeFeet
  )
  const minYBucket = Math.floor(
    (bounds.minYFeet - expansionFeet - grid.yStartFeet) / bucketSizeFeet
  )
  const maxYBucket = Math.floor(
    (bounds.maxYFeet + expansionFeet - grid.yStartFeet) / bucketSizeFeet
  )
  const bucketKeys: string[] = []

  for (let yBucket = minYBucket; yBucket <= maxYBucket; yBucket += 1) {
    for (let xBucket = minXBucket; xBucket <= maxXBucket; xBucket += 1) {
      bucketKeys.push(getCellKey(xBucket, yBucket))
    }
  }

  return bucketKeys
}

export function mergeFeatureBounds(
  existingBounds: ShortTermRateFeatureBounds,
  incomingBounds: ShortTermRateFeatureBounds
): ShortTermRateFeatureBounds {
  return {
    minXFeet: Math.min(existingBounds.minXFeet, incomingBounds.minXFeet),
    maxXFeet: Math.max(existingBounds.maxXFeet, incomingBounds.maxXFeet),
    minYFeet: Math.min(existingBounds.minYFeet, incomingBounds.minYFeet),
    maxYFeet: Math.max(existingBounds.maxYFeet, incomingBounds.maxYFeet),
  }
}

export function compareFeatureCellsByGridPosition(
  firstCell: ShortTermRateFeatureCell,
  secondCell: ShortTermRateFeatureCell
): number {
  return firstCell.y === secondCell.y
    ? firstCell.x - secondCell.x
    : firstCell.y - secondCell.y
}

export function sortFeatureCells(
  featureCells: readonly ShortTermRateFeatureCell[]
): ShortTermRateFeatureCell[] {
  const uniqueCells = new Map<string, ShortTermRateFeatureCell>()
  for (const cell of featureCells) {
    uniqueCells.set(getCellKey(cell.x, cell.y), cell)
  }

  return Array.from(uniqueCells.values()).toSorted(
    compareFeatureCellsByGridPosition
  )
}

export function compareFeatureCellGroups(
  firstCellGroup: readonly ShortTermRateFeatureCell[],
  secondCellGroup: readonly ShortTermRateFeatureCell[]
): number {
  const firstCell = firstCellGroup[0]
  const secondCell = secondCellGroup[0]
  if (!firstCell || !secondCell) {
    return firstCellGroup.length - secondCellGroup.length
  }
  return compareFeatureCellsByGridPosition(firstCell, secondCell)
}

export function getFeatureCellBounds(
  featureCells: readonly ShortTermRateFeatureCell[],
  grid: ShortTermRateFeatureGridMetadata
): ShortTermRateFeatureBounds {
  assertFeatureCellsNotEmpty(featureCells)

  let minXFeet = Number.POSITIVE_INFINITY
  let maxXFeet = Number.NEGATIVE_INFINITY
  let minYFeet = Number.POSITIVE_INFINITY
  let maxYFeet = Number.NEGATIVE_INFINITY

  for (const cell of featureCells) {
    const cellMinXFeet = grid.xStartFeet + cell.x * grid.binWidthFeet
    const cellMaxXFeet = cellMinXFeet + grid.binWidthFeet
    const cellMinYFeet = grid.yStartFeet + cell.y * grid.binHeightFeet
    const cellMaxYFeet = cellMinYFeet + grid.binHeightFeet

    minXFeet = Math.min(minXFeet, cellMinXFeet)
    maxXFeet = Math.max(maxXFeet, cellMaxXFeet)
    minYFeet = Math.min(minYFeet, cellMinYFeet)
    maxYFeet = Math.max(maxYFeet, cellMaxYFeet)
  }

  return { minXFeet, maxXFeet, minYFeet, maxYFeet }
}

export function getBoundsGapFeet(
  firstBounds: ShortTermRateFeatureBounds,
  secondBounds: ShortTermRateFeatureBounds
): number {
  const xGap = Math.max(
    0,
    secondBounds.minXFeet - firstBounds.maxXFeet,
    firstBounds.minXFeet - secondBounds.maxXFeet
  )
  const yGap = Math.max(
    0,
    secondBounds.minYFeet - firstBounds.maxYFeet,
    firstBounds.minYFeet - secondBounds.maxYFeet
  )

  return Math.hypot(xGap, yGap)
}

export function getFeatureCellAreaFeetSquared(
  featureCells: readonly ShortTermRateFeatureCell[],
  grid: ShortTermRateFeatureGridMetadata
): number {
  return featureCells.length * grid.binWidthFeet * grid.binHeightFeet
}

export function getFeatureCellBoundingAreaFeetSquared(
  featureCells: readonly ShortTermRateFeatureCell[],
  grid: ShortTermRateFeatureGridMetadata
): number {
  return getCellBoundsAreaFeetSquared(
    getCellBoundsFromCells(featureCells),
    grid
  )
}

export function getCellBoundsFromCells(
  featureCells: readonly ShortTermRateFeatureCell[]
): ShortTermCorrosionCellBounds {
  assertFeatureCellsNotEmpty(featureCells)

  let minX = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let minY = Number.POSITIVE_INFINITY
  let maxY = Number.NEGATIVE_INFINITY

  for (const cell of featureCells) {
    minX = Math.min(minX, cell.x)
    maxX = Math.max(maxX, cell.x)
    minY = Math.min(minY, cell.y)
    maxY = Math.max(maxY, cell.y)
  }

  return { minX, maxX, minY, maxY }
}

export function getCellBoundsAreaFeetSquared(
  cellBounds: ShortTermCorrosionCellBounds,
  grid: ShortTermRateFeatureGridMetadata
): number {
  return (
    (cellBounds.maxX - cellBounds.minX + 1) *
    grid.binWidthFeet *
    (cellBounds.maxY - cellBounds.minY + 1) *
    grid.binHeightFeet
  )
}

export function getCellKey(x: number, y: number): string {
  return `${x}:${y}`
}

function assertFeatureCellsNotEmpty(
  featureCells: readonly ShortTermRateFeatureCell[]
): void {
  if (featureCells.length === 0) {
    throw new Error('Feature cell bounds require at least one feature cell.')
  }
}
